// Copyright (c) 2020-2021 Visual Intelligence LP. All rights reserved.
//
import Axios from 'axios';
import { injectable } from 'inversify';

import BaseService from '@core/services/base';
import { ListRequestParams, PageInfo } from '@shared/types/services';
import { Cluster, ClusterDTO, ClusterEngineDTO, ClusterEngine } from '@shared/models/clusters';
import { CLUSTER_FIELDS, CLUSTER_ENGINE_FIELDS } from '@shared/constants/clusters';
import { REQUEST_PARAMS } from '@shared/constants/services';

@injectable()
export default class ClustersService extends BaseService<ClusterDTO, Cluster> {
  static diToken = Symbol('cluster-service');

  constructor() {
    super({ domainArea: 'clusters', Model: Cluster });
  }

  getListQuery({ pagination }: ListRequestParams) {
    return `
      {
        clusters(
          ${REQUEST_PARAMS.pagination.fields.page}: ${pagination.page}, 
          ${REQUEST_PARAMS.pagination.fields.pageSize}: ${pagination.pageSize}, 
        ) {
          items {
            ${CLUSTER_FIELDS.clusterName},
            ${CLUSTER_FIELDS.numberOfEngines},
            ${CLUSTER_FIELDS.totalPassMarkScore},
            ${CLUSTER_FIELDS.totalRAM},
            ${CLUSTER_FIELDS.avgCPU},
            ${CLUSTER_FIELDS.avgGPU},
            ${CLUSTER_FIELDS.lastUpdated}
          }
          pageInfo {
            totalCount,
            hasPreviousPage,
            hasNextPage
          }
        }
      }
    `;
  }

  getClusterEnginesQuery(clusterName: ClusterDTO['clusterName'], { pagination }: ListRequestParams) {
    return `
      {
        engines(
          ${REQUEST_PARAMS.pagination.fields.page}: ${pagination.page}, 
          ${REQUEST_PARAMS.pagination.fields.pageSize}: ${pagination.pageSize},
          clusterName: "${clusterName}"
        ) {
          items {
            ${CLUSTER_ENGINE_FIELDS.role},
            ${CLUSTER_ENGINE_FIELDS.engineName},
            ${CLUSTER_ENGINE_FIELDS.passMarkScore},
            ${CLUSTER_ENGINE_FIELDS.currentTaskId},
            ${CLUSTER_ENGINE_FIELDS.numberOfPendingTasks},
            ${CLUSTER_ENGINE_FIELDS.processorName},
            ${CLUSTER_ENGINE_FIELDS.numberOfProcessors},
            ${CLUSTER_ENGINE_FIELDS.ramAmount},
            ${CLUSTER_ENGINE_FIELDS.gpuModels.name} {
              ${CLUSTER_ENGINE_FIELDS.gpuModels.fields.model}, 
              ${CLUSTER_ENGINE_FIELDS.gpuModels.fields.number}
            },
            ${CLUSTER_ENGINE_FIELDS.startDate},
            ${CLUSTER_ENGINE_FIELDS.avgCPU},
            ${CLUSTER_ENGINE_FIELDS.avgGPU},
            ${CLUSTER_ENGINE_FIELDS.lastUpdated}
          }
          pageInfo {
            totalCount,
            hasPreviousPage,
            hasNextPage
          }
        }
      }
    `;
  }

  async getClusterEngines(queries: ListRequestParams, clusterName: ClusterDTO['clusterName']) {
    const url = this.getURL('engines');

    const { data: { engines: { items, pageInfo } } } = await Axios.get<{ engines: { items: Array<ClusterEngineDTO>, pageInfo: PageInfo } }>(
      url,
      { params: { query: this.getClusterEnginesQuery(clusterName, queries) } }
    );

    return {
      pageInfo,
      items: items.map((dto: ClusterEngineDTO) => new ClusterEngine(dto))
    };
  }
}
